/* ═══════════════════════════════════════════════════════════════
   The Designer — document schema + lossless converters.

   The storage format does NOT change: the document on disk is, and
   stays, `ContentBlock[]`. Masters, the diff engine, smart blocks and
   the export pipeline all keep speaking it. This module is the only
   place that translates between that array and the ProseMirror
   document the editor edits.

   Losslessness is a hard requirement, tested as a property:
       docToBlocks(blocksToDoc(x)) ≡ canonical(x)
   Every field we do not model explicitly rides along untouched in the
   node's `meta` attribute, so a block that gains a new field in some
   future version survives a round trip through the Designer unharmed.
   ═══════════════════════════════════════════════════════════════ */

import { Mark, Node as TipTapNode, mergeAttributes } from '@tiptap/core';
import { Document } from '@tiptap/extension-document';
import { Text } from '@tiptap/extension-text';
import { History } from '@tiptap/extension-history';
import { Gapcursor } from '@tiptap/extension-gapcursor';
import { Dropcursor } from '@tiptap/extension-dropcursor';
import type { JSONContent } from '@tiptap/core';
import type { ContentBlock, TextRunSpan } from '../../shared/types';
import {
  effectiveRunsGeneric,
  foldRunsGeneric,
  sameFlags,
  type MarkFlags,
} from '../../shared/text-runs';
import { blockAttrs, newBlockId, newGroupId, type BlockMeta } from './nodeBase';
import { TABLE_NODE_NAME, TABLE_NODES, nodeToTableData, tableDataToNodeJSON } from './tables';

// ─── The fields this schema models explicitly ───
// Anything NOT in this list travels in `meta` and comes back untouched.
// `blockType` rides in meta for paragraphs (the node type carries it for
// tables, images and smart tokens) so a block that says `blockType: 'text'`
// still says exactly that after a round trip.
const MODELLED_FIELDS = [
  'id',
  'level',
  'text',
  'bold',
  'italic',
  'underline',
  'highlight',
  'runs',
  'continuation',
  'excluded',
  'groupId',
  'tableData',
  'imageData',
  'imageWidth',
] as const;

/** Reserved meta keys — bookkeeping, never written back onto the block. */
const EMPHASIS_PRESENCE = '__emphasisKeys';

const EMPHASIS_KEYS = ['bold', 'italic', 'underline', 'highlight'] as const;

function extractMeta(b: ContentBlock, dropBlockType = false): BlockMeta {
  const meta: BlockMeta = {};
  for (const [k, v] of Object.entries(b)) {
    if ((MODELLED_FIELDS as readonly string[]).includes(k)) continue;
    if (dropBlockType && k === 'blockType') continue;
    if (v === undefined) continue;
    meta[k] = v;
  }
  // Remember which emphasis flags the stored block actually carried. A block
  // that says `bold: false` is re-saved saying `bold: false` — the Designer
  // opening a document must not rewrite 452 rows it never edited.
  const present = EMPHASIS_KEYS.filter((k) => Object.prototype.hasOwnProperty.call(b, k));
  if (present.length) meta[EMPHASIS_PRESENCE] = present;
  return meta;
}

/** Strip the bookkeeping keys back out when rebuilding a block. */
function cleanMeta(meta: BlockMeta): BlockMeta {
  const out: BlockMeta = {};
  for (const [k, v] of Object.entries(meta)) {
    if (k.startsWith('__')) continue;
    out[k] = v;
  }
  return out;
}

/** Re-emit the explicit `false` flags the stored block had. */
function restoreEmphasisPresence(
  meta: BlockMeta,
  folded: { bold?: boolean; italic?: boolean; underline?: boolean; highlight?: boolean }
): Record<string, boolean> {
  const present = (meta[EMPHASIS_PRESENCE] as string[] | undefined) || [];
  const out: Record<string, boolean> = {};
  for (const k of EMPHASIS_KEYS) {
    if (present.includes(k) && !folded[k]) out[k] = false;
  }
  return out;
}

// ─── Nodes ───
// `blockAttrs` (id/level/continuation/excluded/meta) lives in nodeBase.ts —
// every direct child of the document, table included, carries it.

/**
 * A spec paragraph. This is the ONLY editable text container — there is no
 * way to type a paragraph that is not a MasterFormat level, which is the
 * product's founding constraint.
 */
export const SpecParagraph = TipTapNode.create({
  name: 'specParagraph',
  group: 'block',
  content: 'inline*',
  defining: true,
  addAttributes: () => ({ ...blockAttrs }),
  parseHTML: () => [{ tag: 'p[data-spec-block]' }],
  renderHTML: ({ HTMLAttributes }) => [
    'p',
    mergeAttributes(HTMLAttributes, { 'data-spec-block': '' }),
    0,
  ],
});

/** An embedded figure. */
export const SpecImage = TipTapNode.create({
  name: 'specImage',
  group: 'block',
  atom: true,
  selectable: true,
  addAttributes: () => ({
    ...blockAttrs,
    text: { default: '' },
    imageData: { default: '' },
    imageWidth: { default: 100 },
  }),
  parseHTML: () => [{ tag: 'div[data-spec-image]' }],
  renderHTML: ({ HTMLAttributes }) => [
    'div',
    mergeAttributes(HTMLAttributes, { 'data-spec-image': '' }),
  ],
});

/**
 * A smart token. Read-only in the Designer by design (§3.4 of the contract
 * cuts smart-block AUTHORING from launch) — it renders resolved and is moved
 * or deleted as one unit.
 */
export const SmartBlockNode = TipTapNode.create({
  name: 'smartBlock',
  group: 'block',
  atom: true,
  selectable: true,
  addAttributes: () => ({
    ...blockAttrs,
    text: { default: '' },
  }),
  parseHTML: () => [{ tag: 'div[data-smart-block]' }],
  renderHTML: ({ HTMLAttributes }) => [
    'div',
    mergeAttributes(HTMLAttributes, { 'data-smart-block': '' }),
  ],
});

// ─── Marks ───
// Deliberately only three. There is no font, size or colour mark: the forma
// owns format, and a user restyles text by changing its LEVEL.

export const Bold = Mark.create({
  name: 'bold',
  parseHTML: () => [{ tag: 'strong' }, { tag: 'b' }],
  renderHTML: ({ HTMLAttributes }) => ['strong', mergeAttributes(HTMLAttributes), 0],
});

export const Italic = Mark.create({
  name: 'italic',
  parseHTML: () => [{ tag: 'em' }, { tag: 'i' }],
  renderHTML: ({ HTMLAttributes }) => ['em', mergeAttributes(HTMLAttributes), 0],
});

export const Underline = Mark.create({
  name: 'underline',
  parseHTML: () => [{ tag: 'u' }],
  renderHTML: ({ HTMLAttributes }) => ['u', mergeAttributes(HTMLAttributes), 0],
});

/**
 * The marker pen. This is MANUAL highlight the author applies — it is stored
 * on the block and prints as Word's `w:highlight`. Revision marks are a
 * different thing entirely: they are computed from the issuance baseline at
 * render time and never stored (see revisions.ts).
 */
export const Highlight = Mark.create({
  name: 'highlight',
  parseHTML: () => [{ tag: 'mark' }],
  renderHTML: ({ HTMLAttributes }) => [
    'mark',
    mergeAttributes(HTMLAttributes, { class: 'dsg-hl' }),
    0,
  ],
});

/** Every extension the document schema needs, in order. */
export const DESIGNER_NODES = [
  Document.extend({ content: 'block+' }),
  Text,
  SpecParagraph,
  ...TABLE_NODES,
  SpecImage,
  SmartBlockNode,
  Bold,
  Italic,
  Underline,
  Highlight,
  Gapcursor,
  Dropcursor,
  History,
];

// ─── Run canonicalisation ───
// The fold/unfold math itself is generic (shared/text-runs.ts) — reused
// verbatim by table cells (tables.ts) and the harvester. These wrappers
// just fix the carrier type to ContentBlock and keep the names every
// existing caller (schema.test.ts included) already imports.

/**
 * The canonical run list for a block: EFFECTIVE emphasis per character,
 * adjacent identical runs merged, empty runs dropped. Both directions of the
 * conversion normalise through this, which is what makes the round trip an
 * equality rather than an approximation.
 */
export function effectiveRuns(b: ContentBlock): Array<{ text: string } & MarkFlags> {
  return effectiveRunsGeneric(b);
}

/**
 * Fold a canonical run list back into ContentBlock's storage shape: emphasis
 * shared by EVERY run becomes a block-level flag, and `runs` is emitted only
 * when something is left over. A uniformly bold paragraph therefore saves
 * exactly as it always has — `{ bold: true }`, no runs.
 */
export function foldRuns(runs: Array<{ text: string } & MarkFlags>): {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  highlight?: boolean;
  runs?: TextRunSpan[];
} {
  return foldRunsGeneric(runs);
}

/** The block as it will exist after a Designer round trip. The ONLY licence
 *  the converter takes is run canonicalisation: adjacent spans with identical
 *  emphasis merge, and emphasis shared by the whole paragraph folds into the
 *  block-level flag. Every other field, including an explicit `bold: false`,
 *  comes back exactly as it went in. */
export function canonicalBlock(b: ContentBlock): ContentBlock {
  if (b.blockType && b.blockType !== 'text') {
    const { runs: _runs, ...rest } = b;
    return { ...rest } as ContentBlock;
  }
  const { bold: _b, italic: _i, underline: _u, highlight: _h, runs: _r, text: _t, ...rest } = b;
  const folded = foldRuns(effectiveRuns(b));
  const explicitFalse: Record<string, boolean> = {};
  for (const k of EMPHASIS_KEYS) {
    if (Object.prototype.hasOwnProperty.call(b, k) && !folded[k]) explicitFalse[k] = false;
  }
  return { ...rest, ...folded, ...explicitFalse, text: folded.text } as ContentBlock;
}

// ─── ContentBlock[] → ProseMirror document ───

function marksFor(flags: MarkFlags): JSONContent['marks'] {
  const marks: NonNullable<JSONContent['marks']> = [];
  if (flags.bold) marks.push({ type: 'bold' });
  if (flags.italic) marks.push({ type: 'italic' });
  if (flags.underline) marks.push({ type: 'underline' });
  if (flags.highlight) marks.push({ type: 'highlight' });
  return marks.length ? marks : undefined;
}

function blockToNode(b: ContentBlock): JSONContent {
  const isAtom = !!b.blockType && b.blockType !== 'text';
  const attrs = {
    blockId: b.id,
    level: Math.max(1, Math.min(9, b.level || 1)),
    continuation: !!b.continuation,
    excluded: !!b.excluded,
    groupId: b.groupId || '',
    // Atoms carry their kind in the node type, so it must not be duplicated
    // in meta; paragraphs keep theirs so `blockType: 'text'` survives.
    meta: extractMeta(b, isAtom),
  };

  switch (b.blockType) {
    case 'table':
      return tableDataToNodeJSON(b, attrs);
    case 'image':
      return {
        type: 'specImage',
        attrs: {
          ...attrs,
          text: b.text || '',
          imageData: b.imageData || '',
          imageWidth: b.imageWidth ?? 100,
        },
      };
    case 'smart-token':
      return { type: 'smartBlock', attrs: { ...attrs, text: b.text || '' } };
    default: {
      const runs = effectiveRuns(b);
      return {
        type: 'specParagraph',
        attrs,
        // An empty paragraph has no content array at all — ProseMirror models
        // "empty" as absence, and an empty text node is invalid.
        ...(runs.length
          ? {
              content: runs.map((r) => ({
                type: 'text',
                text: r.text,
                ...(marksFor(r) ? { marks: marksFor(r) } : {}),
              })),
            }
          : {}),
      };
    }
  }
}

/** The document the editor loads. Never mutates its input. */
export function blocksToDoc(blocks: ContentBlock[]): JSONContent {
  const content = (blocks || []).map(blockToNode);
  return {
    type: 'doc',
    // ProseMirror requires `block+`; an empty spec still needs one paragraph
    // to put the caret in.
    content: content.length
      ? content
      : [
          {
            type: 'specParagraph',
            attrs: {
              blockId: newBlockId(),
              level: 1,
              continuation: false,
              excluded: false,
              groupId: '',
              meta: {},
            },
          },
        ],
  };
}

// ─── ProseMirror document → ContentBlock[] ───

function nodeRuns(node: JSONContent): Array<{ text: string } & MarkFlags> {
  const out: Array<{ text: string } & MarkFlags> = [];
  for (const child of node.content || []) {
    if (child.type !== 'text' || !child.text) continue;
    const names = new Set((child.marks || []).map((m) => m.type));
    const flags: MarkFlags = {
      bold: names.has('bold'),
      italic: names.has('italic'),
      underline: names.has('underline'),
      highlight: names.has('highlight'),
    };
    const last = out[out.length - 1];
    if (last && sameFlags(last, flags)) last.text += child.text;
    else out.push({ text: child.text, ...flags });
  }
  return out;
}

function nodeToBlock(node: JSONContent): ContentBlock | null {
  const attrs = (node.attrs || {}) as {
    blockId?: string;
    level?: number;
    continuation?: boolean;
    excluded?: boolean;
    groupId?: string;
    meta?: BlockMeta;
    text?: string;
    imageData?: string;
    imageWidth?: number;
  };
  const meta = attrs.meta || {};
  const base = {
    ...cleanMeta(meta),
    id: attrs.blockId || newBlockId(),
    level: Math.max(1, Math.min(9, attrs.level || 1)),
    ...(attrs.continuation ? { continuation: true } : {}),
    ...(attrs.excluded ? { excluded: true } : {}),
    ...(attrs.groupId ? { groupId: attrs.groupId } : {}),
  } as ContentBlock;

  switch (node.type) {
    case TABLE_NODE_NAME:
      return {
        ...base,
        blockType: 'table',
        text: attrs.text || '',
        tableData: nodeToTableData(node),
      };
    case 'specImage':
      return {
        ...base,
        blockType: 'image',
        text: attrs.text || '',
        imageData: attrs.imageData || '',
        imageWidth: attrs.imageWidth ?? 100,
      };
    case 'smartBlock':
      return { ...base, blockType: 'smart-token', text: attrs.text || '' };
    case 'specParagraph': {
      const folded = foldRuns(nodeRuns(node));
      return {
        ...base,
        ...folded,
        ...restoreEmphasisPresence(meta, folded),
        text: folded.text,
      };
    }
    default:
      return null;
  }
}

/** The blocks the editor saves. Never mutates its input. */
export function docToBlocks(doc: JSONContent): ContentBlock[] {
  const out: ContentBlock[] = [];
  for (const node of doc?.content || []) {
    const b = nodeToBlock(node);
    if (b) out.push(b);
  }
  return out;
}

// ─── Ids ───

/** Re-exported so every existing `import { newBlockId } from './schema'` keeps
 *  working — the mint itself lives in nodeBase.ts (tables.ts needs it too). */
export { newBlockId, newGroupId };
